import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Boat, GasPump, Anchor, Package, Compass, Sailboat, Wrench, Crane, GlobeHemisphereWest, ArrowUpRight,
} from "@phosphor-icons/react";

const ICONS = { Boat, GasPump, Anchor, Package, Compass, Sailboat, Wrench, Crane, GlobeHemisphereWest };

const SERVICES = [
    { slug: "vessel-chartering", title: "Vessel Chartering", icon: "Boat", summary: "Spot, time and bareboat charters across dry bulk and project cargo." },
    { slug: "tanker-chartering", title: "Tanker Chartering", icon: "GasPump", summary: "Crude, product, chemical and gas tanker chartering with global desks." },
    { slug: "offshore-marine-support", title: "Offshore Marine Support", icon: "Anchor", summary: "AHTS, PSV, crew boats and offshore logistics for upstream operators." },
    { slug: "cargo-transportation", title: "Cargo Transportation", icon: "Package", summary: "Door-to-door multimodal cargo transportation at industrial scale." },
    { slug: "marine-logistics", title: "Marine Logistics", icon: "Compass", summary: "Lashing, stowage and transit planning across complex corridors." },
    { slug: "shipbuilding", title: "Shipbuilding", icon: "Sailboat", summary: "New-build management, shipyard supervision and class certification." },
    { slug: "ship-repairs", title: "Ship Repairs & Maintenance", icon: "Wrench", summary: "Drydock coordination, scheduled and emergency repairs worldwide." },
    { slug: "port-operations", title: "Port Operations", icon: "Crane", summary: "Stevedoring, husbandry, bunkering, 24/7 port agency services." },
    { slug: "freight-coordination", title: "Freight Coordination", icon: "GlobeHemisphereWest", summary: "Brokerage, voyage estimation and post-fixture management." },
];

export default function ServicesGrid({ heading = true, compact = false }) {
    return (
        <section data-testid="services-grid" className={`relative ${compact ? "py-16" : "py-24 md:py-32"} bg-[#040914]`}>
            <div className="wms-container">
                {heading && (
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div className="max-w-2xl">
                            <span className="wms-overline">Capabilities — 02</span>
                            <h2 className="mt-5 font-display font-light text-4xl md:text-5xl leading-tight tracking-tight">
                                Nine core service desks. <span className="italic text-[#48CAE4]">One operations spine.</span>
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            data-testid="services-grid-cta"
                            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-white/70 hover:text-white"
                        >
                            View full services <ArrowUpRight size={16} weight="bold" />
                        </Link>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
                    {SERVICES.map((s, idx) => {
                        const Icon = ICONS[s.icon] || Boat;
                        return (
                            <motion.div
                                key={s.slug}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: idx * 0.04 }}
                                className="group relative bg-[#040914] hover:bg-[#0B1324] transition-colors p-8 min-h-[260px] flex flex-col justify-between"
                                data-testid={`service-card-${s.slug}`}
                            >
                                <div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] tracking-[0.32em] uppercase text-white/40">
                                            {String(idx + 1).padStart(2, "0")}
                                        </span>
                                        <span className="flex items-center justify-center w-12 h-12 border border-white/10 group-hover:border-[#48CAE4]/50 transition-colors">
                                            <Icon size={22} weight="duotone" className="text-[#48CAE4]" />
                                        </span>
                                    </div>
                                    <h3 className="mt-8 font-display text-xl text-white leading-tight">{s.title}</h3>
                                    <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.summary}</p>
                                </div>

                                <Link
                                    to={`/services#${s.slug}`}
                                    data-testid={`service-link-${s.slug}`}
                                    className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#48CAE4] hover:text-white transition-colors"
                                >
                                    Inquire desk
                                    <ArrowUpRight size={12} weight="bold" />
                                </Link>

                                <span className="pointer-events-none absolute inset-0 border border-transparent group-hover:border-[#48CAE4]/20 transition-colors" />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
